/**
 * Catégories produits telles que saisies dans les caisses : la même catégorie
 * revient sous plusieurs graphies d'une boutique à l'autre (« FLEURS » /
 * « Fleurs », « Cosmétiques » / « COSMETIQUES » / « COSMÉTIQUE », « Huiles &
 * Gélules » / « HUILES / GÉLULES »). Le filtre les regroupe sous une seule clé.
 */

/** Petits mots ignorés : « Fleurs de CBD » = « Fleur cbd », « Huiles & Gélules » = « HUILES / GÉLULES ». */
const STOP_WORDS = new Set(["et", "de", "du", "des", "la", "le", "les"]);

/**
 * Clé de regroupement : sans accents, casse ni ponctuation, sans petits mots,
 * mots au singulier et lettres doublées simplifiées (« ACCESOIRES » = « ACCESSOIRES »).
 */
export function categoryKey(category: string): string {
  return category
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9+]+/g, " ")
    .trim()
    .split(" ")
    .filter((w) => w && !STOP_WORDS.has(w))
    .map((w) => (w.length > 3 ? w.replace(/[sx]$/, "") : w).replace(/([a-z])\1/g, "$1"))
    .join(" ");
}

export interface CategoryOption {
  key: string;
  label: string;
  count: number;
}

const isAllCaps = (s: string) => s === s.toUpperCase() && s !== s.toLowerCase();

/**
 * Catégories regroupées, triées par ordre alphabétique. Libellé affiché : la
 * graphie la plus fréquente, en préférant une graphie qui n'est pas tout en capitales.
 */
export function groupCategories(categories: (string | null | undefined)[]): CategoryOption[] {
  const groups = new Map<string, { count: number; labels: Map<string, number> }>();
  for (const c of categories) {
    const label = c?.trim();
    if (!label) continue;
    const key = categoryKey(label);
    const g = groups.get(key) ?? { count: 0, labels: new Map<string, number>() };
    g.count++;
    g.labels.set(label, (g.labels.get(label) ?? 0) + 1);
    groups.set(key, g);
  }
  return [...groups.entries()]
    .map(([key, g]) => {
      const label = [...g.labels.entries()].sort((a, b) => Number(isAllCaps(a[0])) - Number(isAllCaps(b[0])) || b[1] - a[1])[0][0];
      return { key, label, count: g.count };
    })
    .sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));
}
